import type {
  SimilarSupportTicket,
  SupportTicketOwn,
} from '@support/entities/support-ticket';
import { useAuthData } from '@auth/features/auth';
import {
  SupportStatusBadge,
  SupportTypeBadge,
  TicketAttachments,
  useMyTickets,
  useOwnTicketEditWindow,
  useRecentTickets,
  useSupportDrawerStore,
  useToggleVote,
} from '@support/entities/support-ticket';
import { TicketConversation } from '@support/features/ticket-conversation';
import {
  CreateSupportTicketModal,
  EditSupportTicketModal,
  formatEditTimeLeft,
} from '@support/features/ticket-create';
import { useNavigate } from '@tanstack/react-router';
import {
  ChevronDown,
  Headset,
  Lock,
  Pencil,
  Plus,
  Settings2,
  ThumbsUp,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import { Button, EmptyState, Skeleton, SlidePanel, Text } from '@/shared/ui';
import css from './support-drawer.module.scss';

const ListSkeleton = () => (
  <div className={css.list}>
    {Array.from({ length: 4 }, (_, i) => `skeleton-${i}`).map((id) => (
      <div className={css.item} key={id}>
        <Skeleton width="70%" />
        <Skeleton width="40%" />
      </div>
    ))}
  </div>
);

// What the reporter filed (description + attachments), and — for the short
// grace window after filing — the way back into the form. Once the window
// closes the ticket is locked and further detail goes in the comments.
const TicketDetails = ({ ticket }: { ticket: SupportTicketOwn }) => {
  const msLeft = useOwnTicketEditWindow(ticket);
  const [editing, setEditing] = useState(false);
  const editable = msLeft > 0;

  return (
    <>
      <Text className={css.description} size="sm">
        {ticket.description}
      </Text>
      <TicketAttachments
        attachments={ticket.attachments}
        title={ticket.title}
      />
      <div className={css.editBar}>
        {editable ? (
          <>
            <Text size="xs" variant="muted">
              {formatEditTimeLeft(msLeft)}
            </Text>
            <Button
              icon={<Pencil style={{ height: 14, width: 14 }} />}
              onClick={() => setEditing(true)}
              size="sm"
              variant="outline"
            >
              Edit ticket
            </Button>
          </>
        ) : (
          <Text className={css.locked} size="xs" variant="muted">
            <Lock aria-hidden="true" style={{ height: 12, width: 12 }} />
            Locked for editing — add anything else as a comment below.
          </Text>
        )}
      </div>
      <EditSupportTicketModal
        onClose={() => setEditing(false)}
        open={editing}
        ticket={ticket}
      />
    </>
  );
};

// A reporter's own ticket, expandable in place to reveal what was filed and
// its comments. Expand-in-place (rather than a second nested panel) mirrors
// the notification drawer's rows, and costs a single comments query and no new
// procedure.
const TicketRow = ({ ticket }: { ticket: SupportTicketOwn }) => {
  const panelId = useId();
  const [expanded, setExpanded] = useState(false);
  const rowRef = useRef<HTMLLIElement>(null);
  const focusTicketId = useSupportDrawerStore((s) => s.focusTicketId);
  const clearFocusTicket = useSupportDrawerStore((s) => s.clearFocusTicket);

  // Deep entry (the notification bell's "View details"): open this ticket's
  // thread and bring it into view. The focus is cleared as soon as it is
  // consumed, so collapsing the row afterwards sticks.
  const isFocused = focusTicketId === ticket.id;
  useEffect(() => {
    if (!isFocused) return;
    setExpanded(true);
    rowRef.current?.scrollIntoView({ block: 'nearest' });
    clearFocusTicket();
  }, [isFocused, clearFocusTicket]);

  return (
    <li className={joinCss(css.item, css.itemExpandable)} ref={rowRef}>
      <button
        aria-controls={panelId}
        aria-expanded={expanded}
        className={css.itemToggle}
        onClick={() => setExpanded((open) => !open)}
        type="button"
      >
        <div className={css.itemInfo}>
          <Text weight="medium">{ticket.title}</Text>
          <div className={css.itemMeta}>
            {/* The reference the reporter quotes when following up. */}
            {ticket.ticketRef ? (
              <Text as="span" className={css.itemRef} size="xs" variant="muted">
                {ticket.ticketRef}
              </Text>
            ) : null}
            <SupportTypeBadge type={ticket.type} />
            <SupportStatusBadge status={ticket.status} />
            <Text as="span" size="xs" variant="muted">
              {ticket.createdOn.slice(0, 10)}
            </Text>
          </div>
        </div>
        <ChevronDown
          aria-hidden="true"
          className={joinCss(css.chevron, expanded && css.chevronOpen)}
        />
      </button>

      {expanded ? (
        // The row header above already carries the title, type, status and
        // date, so the panel starts at the description rather than repeating
        // the title.
        <div className={css.itemPanel} id={panelId}>
          <TicketDetails ticket={ticket} />
          {/* Same component the management drawer uses — one comments
              implementation, and the server decides what each side may see. */}
          <TicketConversation enabled={expanded} ticketId={ticket.id} />
        </div>
      ) : null}
    </li>
  );
};

// The reporter's own tickets — compact card list.
const MyTickets = ({ enabled }: { enabled: boolean }) => {
  const { data, isLoading } = useMyTickets(enabled);
  const rows = data?.data ?? [];

  if (isLoading) return <ListSkeleton />;
  if (rows.length === 0) {
    return (
      <EmptyState
        description="Tickets you report will appear here."
        icon={<Headset style={{ height: 22, width: 22 }} />}
        title="No support tickets yet"
      />
    );
  }
  return (
    <ul className={css.list}>
      {rows.map((ticket) => (
        <TicketRow key={ticket.id} ticket={ticket} />
      ))}
    </ul>
  );
};

const RecentTicketRow = ({
  onVote,
  pending,
  ticket,
}: {
  onVote: () => void;
  pending: boolean;
  ticket: SimilarSupportTicket;
}) => (
  <li className={css.item}>
    <div className={css.itemInfo}>
      <Text weight="medium">{ticket.title}</Text>
      <div className={css.itemMeta}>
        {ticket.ticketRef ? (
          <Text as="span" className={css.itemRef} size="xs" variant="muted">
            {ticket.ticketRef}
          </Text>
        ) : null}
        <SupportTypeBadge type={ticket.type} />
        <SupportStatusBadge status={ticket.status} />
        <Text as="span" size="xs" variant="muted">
          {ticket.createdOn.slice(0, 10)}
        </Text>
      </div>
    </div>
    <button
      aria-label={`Upvote ${ticket.title}`}
      aria-pressed={ticket.hasVoted}
      className={joinCss(css.vote, ticket.hasVoted && css.voteActive)}
      disabled={pending}
      onClick={onVote}
      type="button"
    >
      <ThumbsUp
        className={css.voteIcon}
        fill={ticket.hasVoted ? 'currentColor' : 'none'}
      />
      {ticket.voteCount}
    </button>
  </li>
);

// Cross-reporter recent tickets — minimal projection, same upvote affordance
// as the create-ticket similar-tickets list so a reporter can back an
// existing ticket from here too instead of filing a duplicate.
const RecentTickets = ({ enabled }: { enabled: boolean }) => {
  const { data, isLoading } = useRecentTickets(enabled);
  const vote = useToggleVote();
  const rows = data ?? [];

  if (isLoading) return <ListSkeleton />;
  if (rows.length === 0) {
    return (
      <EmptyState
        description="Tickets reported across your organization will appear here."
        icon={<Headset style={{ height: 22, width: 22 }} />}
        title="No recent tickets"
      />
    );
  }
  return (
    <ul className={css.list}>
      {rows.map((ticket) => (
        <RecentTicketRow
          key={ticket.id}
          onVote={() => vote.mutate({ id: ticket.id })}
          pending={vote.isPending && vote.variables?.id === ticket.id}
          ticket={ticket}
        />
      ))}
    </ul>
  );
};

// Mounted once at the AppLayout root so it overlays every page. "My tickets"
// shows the reporter's own tickets; "Recent" shows the org's latest tickets
// (minimal cross-reporter projection) with an upvote action. Distinct from
// IncidentDrawer by design: own Headset iconography, a blue "info" accent,
// and real My/Recent tabs (vs. incidents' My/All navigation toggle) — this is
// a separate domain, not a reskin.
export const SupportDrawer = () => {
  const open = useSupportDrawerStore((s) => s.open);
  const closeDrawer = useSupportDrawerStore((s) => s.closeDrawer);
  const openReport = useSupportDrawerStore((s) => s.openReport);
  const activeTab = useSupportDrawerStore((s) => s.activeTab);
  const setTab = useSupportDrawerStore((s) => s.setTab);
  const { data: authData } = useAuthData();
  const navigate = useNavigate();
  // Operation/Admin (support_tickets:read:any) get a shortcut into the full
  // management table. The direct /support-management route is independently
  // RBAC-gated, so this button is a discoverability aid, not the guard.
  const canManage =
    authData?.user.permissions.includes('support_tickets:read:any') ?? false;
  const goToManagement = () => {
    closeDrawer();
    navigate({ to: '/support-management' });
  };

  return (
    <>
      <SlidePanel
        hideScrollbar
        onClose={closeDrawer}
        open={open}
        title="Support"
        width={720}
      >
        <div className={css.body}>
          <div className={css.toolbar}>
            {/* biome-ignore lint/a11y/useSemanticElements: styled segmented control (My/Recent), not a form fieldset */}
            <div
              aria-label="Ticket scope"
              className={css.segmented}
              role="group"
            >
              <button
                aria-pressed={activeTab === 'my'}
                className={joinCss(
                  css.segment,
                  activeTab === 'my' && css.segmentActive
                )}
                onClick={() => setTab('my')}
                type="button"
              >
                My tickets
              </button>
              <button
                aria-pressed={activeTab === 'recent'}
                className={joinCss(
                  css.segment,
                  activeTab === 'recent' && css.segmentActive
                )}
                onClick={() => setTab('recent')}
                type="button"
              >
                Recent
              </button>
            </div>
            <div className={css.actions}>
              {canManage ? (
                <Button
                  icon={<Settings2 style={{ height: 14, width: 14 }} />}
                  onClick={goToManagement}
                  size="sm"
                  variant="outline"
                >
                  Manage tickets
                </Button>
              ) : null}
              <Button
                icon={<Plus style={{ height: 14, width: 14 }} />}
                onClick={() => openReport()}
                size="sm"
                variant="primary"
              >
                Create support ticket
              </Button>
            </div>
          </div>

          {activeTab === 'my' ? (
            <MyTickets enabled={open && activeTab === 'my'} />
          ) : (
            <RecentTickets enabled={open && activeTab === 'recent'} />
          )}
        </div>
      </SlidePanel>

      <CreateSupportTicketModal />
    </>
  );
};
